import type React from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';

import { getSerpPageUrl } from '../../../helpers';

import * as S from './pagination.styles';

type PaginationItem = number | 'ellipsis';

type PaginationProps = {
  currentPage: number;
  pathname: string;
  search: string;
  totalPages: number;
};

const getPaginationItems = (
  currentPage: number,
  totalPages: number
): PaginationItem[] => {
  const windowDelta = 1;
  const windowSize = 1 + windowDelta * 2;
  const rightCroppingLimit = 1 + windowSize;
  const leftCroppingLimit = totalPages - windowSize;
  const right = Math.max(currentPage, rightCroppingLimit) + windowDelta;
  const left = Math.min(currentPage, leftCroppingLimit) - windowDelta;

  const range = [1];

  if (totalPages > 1) {
    for (let page = left; page <= right; page++) {
      if (page > 1 && page < totalPages) {
        range.push(page);
      }
    }

    range.push(totalPages);
  }

  return range.flatMap((page, index) => {
    const previousPage = range[index - 1];

    if (previousPage && page - previousPage === 2) {
      return [previousPage + 1, page];
    }

    if (previousPage && page - previousPage > 2) {
      return ['ellipsis' as const, page];
    }

    return [page];
  });
};

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pathname,
  search,
  totalPages,
}) => {
  const { formatMessage } = useIntl();
  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (totalPages <= 1) {
    return null;
  }

  const previousPage = currentPage - 1;
  const nextPage = currentPage + 1;

  return (
    <S.Wrapper aria-label={formatMessage({ id: 'JOBS_SEARCH_PAGINATION' })}>
      <S.PaginationList>
        <Chevron
          aria-label={formatMessage({ id: 'JOBS_SEARCH_PAGINATION_PREVIOUS' })}
          disabled={currentPage === 1}
          page={previousPage}
          pathname={pathname}
          search={search}
          direction="left"
        />
        {paginationItems.map((item, index) =>
          item === 'ellipsis' ? (
            <S.Ellipsis key={`ellipsis-${index}`}>...</S.Ellipsis>
          ) : (
            <Page
              key={item}
              currentPage={currentPage}
              page={item}
              pathname={pathname}
              search={search}
            />
          )
        )}
        <Chevron
          aria-label={formatMessage({ id: 'JOBS_SEARCH_PAGINATION_NEXT' })}
          disabled={currentPage === totalPages}
          page={nextPage}
          pathname={pathname}
          search={search}
          direction="right"
        />
      </S.PaginationList>
    </S.Wrapper>
  );
};

type PageProps = {
  currentPage: number;
  page: number;
  pathname: string;
  search: string;
};

const Page: React.FC<PageProps> = ({ currentPage, page, pathname, search }) => {
  const isCurrent = page === currentPage;

  return (
    <li aria-label={page.toString()}>
      <TextButton
        aria-current={isCurrent ? 'page' : undefined}
        disabled={isCurrent}
        size="large"
        to={isCurrent ? undefined : getSerpPageUrl({ pathname, page, search })}
      >
        {page}
      </TextButton>
    </li>
  );
};

type ChevronProps = {
  'aria-label': string;
  direction: 'left' | 'right';
  disabled: boolean;
  page: number;
  pathname: string;
  search: string;
};

const Chevron: React.FC<ChevronProps> = ({
  direction,
  disabled,
  page,
  pathname,
  search,
  ...props
}) => {
  const icon = direction === 'left' ? IconArrowLeft : IconArrowRight;
  const rel = direction === 'left' ? 'prev' : 'next';

  return (
    <li>
      <TextButton
        disabled={disabled}
        icon={icon}
        rel={rel}
        size="large"
        to={!disabled ? getSerpPageUrl({ pathname, page, search }) : undefined}
        {...props}
      />
    </li>
  );
};
